import type {
  TournamentCompetition,
  TournamentCompetitionConflict,
  TournamentCompetitionEvent,
  TournamentEntry,
} from "@mixitone/models";
import { deepEqual } from "@mixitone/util";
import { getFormat } from "tournament-formats";
import type { FormatName } from "tournament-formats";
import {
  type Entry,
  type Match,
  type TournamentState,
  type Winner,
  blankTournamentState,
} from "tournament-formats/types";
import {
  detectVoidResultConflict,
  detectWinnerConflict,
  dropWinnerConflictsForSlot,
  isAuthoritativeWinnerPayload,
  mergeOpenConflicts,
  type TournamentConflict,
} from "./TournamentConflictDetector";
import { getMatchSlotId, type MatchProjectionMetadata } from "./TournamentEventTypes";

interface WinnerVote {
  winnerId: string;
  event: TournamentCompetitionEvent;
}

interface SlotState {
  forced_entry_ids?: [string | undefined, string | undefined];
  status?: "pending" | "ready" | "in_progress" | "completed" | "voided";
  court_id?: string;
  score?: Record<string, unknown>;
  winner_id?: string;
  round?: number;
  match_index?: number;
  division?: number;
  source_event?: TournamentCompetitionEvent;
  started_at?: Date;
  completed_at?: Date;
  voided_at?: Date;
  void_reason?: string;
  void_responsible_entry_ids?: string[];
  /** Last winner vote per client during reduce. Not projected. */
  winnerVotes?: Map<string, WinnerVote>;
}

export interface ReducedTournament {
  state: TournamentState;
  conflicts: TournamentConflict[];
}

const SLOT_ID_PATTERN = /^r(\d+):m(\d+)(?::d(\d+))?$/;

function asFiniteNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return undefined;
  }
  return value;
}

function parseSlotCoordinatesFromId(slotId: string) {
  const match = slotId.match(SLOT_ID_PATTERN);
  if (!match) return undefined;
  return {
    round: Number.parseInt(match[1], 10),
    matchIndex: Number.parseInt(match[2], 10),
    division: match[3] ? Number.parseInt(match[3], 10) : undefined,
  };
}

function resolveSlotCoordinates(
  slot: { round?: unknown; match_index?: unknown; division?: unknown },
  slotId: string,
) {
  const parsedFromPayload = {
    round: asFiniteNumber(slot.round),
    matchIndex: asFiniteNumber(slot.match_index),
    division: asFiniteNumber(slot.division),
  };

  const parsedFromSlotId = parseSlotCoordinatesFromId(slotId);
  if (parsedFromSlotId) {
    return {
      ...parsedFromSlotId,
      division: parsedFromPayload.division ?? parsedFromSlotId.division,
    };
  }
  if (parsedFromPayload.round !== undefined && parsedFromPayload.matchIndex !== undefined)
    return parsedFromPayload;
  return undefined;
}

function jsonRecord(value: unknown): Record<string, unknown> {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function winnerVoteClientKey(event: TournamentCompetitionEvent): string {
  return event.client_id || `event:${event.id ?? "unknown"}`;
}

function uniqueWinnerIds(votes: Map<string, WinnerVote>): string[] {
  const ids: string[] = [];
  for (const vote of votes.values()) {
    if (!ids.includes(vote.winnerId)) ids.push(vote.winnerId);
  }
  return ids;
}

function lastVoteForWinner(votes: Map<string, WinnerVote>, winnerId: string): WinnerVote | undefined {
  let last: WinnerVote | undefined;
  for (const vote of votes.values()) {
    if (vote.winnerId !== winnerId) continue;
    if (!last) {
      last = vote;
      continue;
    }
    const lastTime = last.event.created_at?.getTime() ?? 0;
    const voteTime = vote.event.created_at?.getTime() ?? 0;
    if (voteTime >= lastTime) last = vote;
  }
  return last;
}

function buildInitialState(entries: TournamentEntry[], metadata?: Record<string, unknown>): TournamentState {
  const seededEntries: Entry[] = entries.map((entry) => {
    const formatData = jsonRecord(entry.format_data);
    const rawFormatData = jsonRecord(entry.rawAttributes?.format_data);
    const pendingOrDirty =
      Boolean(entry.changedAttributes?.has?.("format_data")) ||
      Boolean(entry.pendingAttributes?.has?.("format_data"));
    const merged = pendingOrDirty ? { ...rawFormatData, ...formatData } : { ...formatData, ...rawFormatData };
    const group = entry.group ?? merged.group;
    const division = pendingOrDirty
      ? (entry.division ?? merged.division)
      : (merged.division ?? entry.division);
    return [
      entry.id!,
      entry.seed || 999,
      {
        ...merged,
        ...(group !== undefined ? { group } : {}),
        ...(division !== undefined ? { division } : {}),
      },
    ];
  });

  const winnersFromMetadata: Winner[] | undefined = Array.isArray(metadata?.winners)
    ? (metadata.winners as unknown[]).filter((winner): winner is Winner => {
        if (!Array.isArray(winner) || winner.length < 1) return false;
        return typeof winner[0] === "string";
      })
    : undefined;

  return {
    ...blankTournamentState(),
    entries: seededEntries,
    winners: winnersFromMetadata,
    metadata: metadata || {},
  };
}

function sortEvents(events: TournamentCompetitionEvent[]) {
  return [...events].toSorted((a, b) => {
    const aTime = a.created_at?.getTime() ?? 0;
    const bTime = b.created_at?.getTime() ?? 0;
    if (aTime !== bTime) return aTime - bTime;
    return (a.id || "").localeCompare(b.id || "");
  });
}

function applySlotState(match: Match, roundIndex: number, matchIndex: number, slots: Map<string, SlotState>) {
  const division =
    typeof match[2]?.division === "number"
      ? Number(match[2]?.division)
      : typeof match[2]?.division === "string"
        ? Number(match[2]?.division)
        : undefined;
  const safeDivision = Number.isNaN(division) ? undefined : division;
  const positionalSlotId = getMatchSlotId(roundIndex, matchIndex, safeDivision);
  const barePositionalSlotId = getMatchSlotId(roundIndex, matchIndex);
  const declaredSlotId =
    typeof match[2]?.slot_id === "string" && SLOT_ID_PATTERN.test(match[2].slot_id)
      ? match[2].slot_id
      : undefined;
  const posSlot = slots.get(positionalSlotId);
  // Events and projected match ids use `r{n}:m{m}` (see TournamentProjection) while
  // divided brackets resolve slots as `r{n}:m{m}:d{d}` — read both and merge so a
  // `match_entries_set` on the divided key does not mask `winner_selected` on the bare key.
  const barePosSlot = safeDivision !== undefined ? slots.get(barePositionalSlotId) : undefined;
  const decSlot = declaredSlotId ? slots.get(declaredSlotId) : undefined;

  let slot: SlotState | undefined;
  let resolvedSlotId: string;

  if (posSlot && barePosSlot && posSlot !== barePosSlot) {
    slot = {
      ...barePosSlot,
      ...posSlot,
      winner_id: posSlot.winner_id ?? barePosSlot.winner_id,
      status:
        posSlot.winner_id || barePosSlot.winner_id ? "completed" : (posSlot.status ?? barePosSlot.status),
      forced_entry_ids: posSlot.forced_entry_ids ?? barePosSlot.forced_entry_ids,
    };
    resolvedSlotId = barePosSlot.winner_id && !posSlot.winner_id ? barePositionalSlotId : positionalSlotId;
  } else {
    slot = posSlot ?? barePosSlot;
    resolvedSlotId = barePosSlot && !posSlot ? barePositionalSlotId : positionalSlotId;
  }

  const useDeclaredFallback =
    !!declaredSlotId &&
    declaredSlotId !== positionalSlotId &&
    declaredSlotId !== barePositionalSlotId &&
    !slot &&
    !!decSlot;
  if (useDeclaredFallback) {
    slot = decSlot;
    resolvedSlotId = declaredSlotId!;
  }

  if (!slot) {
    match[2] = {
      ...match[2],
      slot_id: declaredSlotId ?? barePositionalSlotId,
      round: roundIndex,
      match_index: matchIndex,
      division: safeDivision,
    };
    return;
  }

  const metadata: MatchProjectionMetadata = {
    ...match[2],
    slot_id: resolvedSlotId,
    round: roundIndex,
    match_index: matchIndex,
    division: safeDivision,
    status: slot.status,
    court_id: slot.court_id,
    score: slot.score,
    started_at: slot.started_at,
    completed_at: slot.completed_at,
    voided_at: slot.voided_at,
    void_reason: slot.void_reason as MatchProjectionMetadata["void_reason"],
    void_responsible_entry_ids: slot.void_responsible_entry_ids,
  };
  match[2] = metadata;

  const preForced0 = match[0]?.[0];
  const preForced1 = match[1]?.[0];

  if (slot.forced_entry_ids) {
    const [entry1, entry2] = slot.forced_entry_ids;
    match[0] = entry1 ? [entry1, !!(slot.winner_id && slot.winner_id === entry1)] : null;
    match[1] = entry2 ? [entry2, !!(slot.winner_id && slot.winner_id === entry2)] : null;
  }

  if (slot.winner_id && match[0]?.[0] && match[1]?.[0]) {
    const entry1Wins = match[0][0] === slot.winner_id;
    const entry2Wins = match[1][0] === slot.winner_id;
    if (entry1Wins || entry2Wins) {
      match[0] = [match[0][0], entry1Wins];
      match[1] = [match[1][0], entry2Wins];
      metadata.status = "completed";
    } else if (preForced0 && preForced1) {
      // `match_entries_set` can disagree with bracket order; still record the winner if it
      // matches the sides the format had before slot state was applied.
      const pre1Wins = preForced0 === slot.winner_id;
      const pre2Wins = preForced1 === slot.winner_id;
      if (pre1Wins || pre2Wins) {
        match[0] = [preForced0, pre1Wins];
        match[1] = [preForced1, pre2Wins];
        metadata.status = "completed";
      }
    }
  }
}

function applyPlayoffDivisionEvent(event: TournamentCompetitionEvent, entries: Entry[]) {
  if (event.event_type === "playoff_divisions_reset") {
    for (const entry of entries) {
      if (!entry[2] || entry[2].division === undefined) continue;
      const next = { ...entry[2] };
      delete next.division;
      entry[2] = next;
    }
    return;
  }

  if (event.event_type !== "playoff_division_set") return;

  const payload = event.payload || {};
  const entryId = typeof payload.entry_id === "string" ? payload.entry_id : undefined;
  if (!entryId) return;
  const entry = entries.find((candidate) => candidate[0] === entryId);
  if (!entry) return;

  const next = { ...(entry[2] || {}) };
  const raw = payload.division;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 1) {
    next.division = raw;
  } else {
    delete next.division;
  }
  entry[2] = next;
}

function applyEventToSlots(
  event: TournamentCompetitionEvent,
  slots: Map<string, SlotState>,
  conflicts: TournamentConflict[],
) {
  const payload = event.payload || {};
  const slot = payload.slot as
    | {
        slot_id?: string;
        round?: unknown;
        match_index?: unknown;
        division?: unknown;
      }
    | undefined;
  const slotId = slot?.slot_id;
  if (!slotId) {
    return;
  }

  const current = slots.get(slotId) || {};
  const slotCoordinates = resolveSlotCoordinates(slot || {}, slotId);
  const nextSlotCoordinates = {
    round: slotCoordinates?.round ?? current.round,
    match_index: slotCoordinates?.matchIndex ?? current.match_index,
    division: slotCoordinates?.division ?? current.division,
  };

  if (event.event_type === "match_completed") {
    if (current.status === "voided") {
      const conflict = detectVoidResultConflict({
        existingEvent: current.source_event || null,
        existingStatus: current.status,
        existingWinnerId: current.winner_id,
        nextEvent: event,
        slotId,
      });
      if (conflict) conflicts.push(conflict);
      return;
    }
    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      status: "completed",
      score:
        typeof payload.score === "object" && payload.score !== null
          ? (payload.score as Record<string, unknown>)
          : current.score,
      // First completion wins so later scoring does not extend stand-down.
      completed_at: current.completed_at ?? event.created_at,
      source_event: event,
    });
    return;
  }

  if (event.event_type === "match_voided") {
    // Terminal once voided: later assign/start events are ignored. Concurrent
    // results become conflicts instead of silent drops.
    if (current.status === "voided") return;
    if (current.status === "completed" || current.winner_id) {
      const conflict = detectVoidResultConflict({
        existingEvent: current.source_event || null,
        existingStatus: current.status,
        existingWinnerId: current.winner_id,
        nextEvent: event,
        slotId,
      });
      if (conflict) conflicts.push(conflict);
      return;
    }

    const reason = typeof payload.reason === "string" ? payload.reason : undefined;
    const responsible = Array.isArray(payload.responsible_entry_ids)
      ? payload.responsible_entry_ids.filter((id): id is string => typeof id === "string")
      : undefined;

    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      status: "voided",
      court_id: undefined,
      completed_at: undefined,
      voided_at: current.voided_at ?? event.created_at,
      void_reason: reason ?? current.void_reason,
      void_responsible_entry_ids: responsible ?? current.void_responsible_entry_ids,
      source_event: event,
    });
    return;
  }

  if (event.event_type === "match_result_recorded" || event.event_type === "winner_selected") {
    if (current.status === "voided") {
      const conflict = detectVoidResultConflict({
        existingEvent: current.source_event || null,
        existingStatus: current.status,
        existingWinnerId: current.winner_id,
        nextEvent: event,
        slotId,
      });
      if (conflict) conflicts.push(conflict);
      return;
    }
    const winnerId = typeof payload.winner_id === "string" ? payload.winner_id : undefined;
    const score =
      typeof payload.score === "object" && payload.score !== null
        ? (payload.score as Record<string, unknown>)
        : current.score;

    const applyWinner = (nextWinnerId: string | undefined, sourceEvent: TournamentCompetitionEvent) => {
      slots.set(slotId, {
        ...current,
        ...nextSlotCoordinates,
        status: "completed",
        winner_id: nextWinnerId,
        score,
        completed_at: current.completed_at ?? event.created_at,
        source_event: sourceEvent,
        winnerVotes: current.winnerVotes,
      });
    };

    if (!winnerId) {
      applyWinner(current.winner_id, event);
      return;
    }

    if (isAuthoritativeWinnerPayload(payload)) {
      current.winnerVotes = new Map([[winnerVoteClientKey(event), { winnerId, event }]]);
      dropWinnerConflictsForSlot(conflicts, slotId);
      applyWinner(winnerId, event);
      return;
    }

    const votes = current.winnerVotes ?? new Map<string, WinnerVote>();
    votes.set(winnerVoteClientKey(event), { winnerId, event });
    current.winnerVotes = votes;

    const uniqueWinners = uniqueWinnerIds(votes);
    if (uniqueWinners.length <= 1) {
      const applied = lastVoteForWinner(votes, uniqueWinners[0] ?? winnerId);
      dropWinnerConflictsForSlot(conflicts, slotId);
      applyWinner(applied?.winnerId ?? winnerId, applied?.event ?? event);
      return;
    }

    const conflict = detectWinnerConflict({
      existingEvent: current.source_event || null,
      existingWinnerId: current.winner_id,
      nextEvent: event,
      nextWinnerId: winnerId,
      slotId,
    });
    if (conflict) {
      const existingForSlot = conflicts.findIndex(
        (candidate) => candidate.conflict_type === "winner_conflict" && candidate.payload?.slot_id === slotId,
      );
      if (existingForSlot >= 0) {
        conflicts[existingForSlot] = conflict;
      } else {
        conflicts.push(conflict);
      }
      if (!current.winner_id) {
        applyWinner(winnerId, event);
      } else {
        slots.set(slotId, {
          ...current,
          ...nextSlotCoordinates,
          winnerVotes: votes,
        });
      }
      return;
    }

    applyWinner(winnerId, event);
    return;
  }

  if (event.event_type === "match_assigned") {
    if (current.status === "voided") return;
    const nextStatus =
      current.status === "in_progress" || current.status === "completed" ? current.status : "ready";
    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      court_id: typeof payload.court_id === "string" ? payload.court_id : current.court_id,
      status: nextStatus,
      source_event: event,
    });
    return;
  }

  if (event.event_type === "match_entries_set") {
    if (current.status === "voided") return;
    const entries = Array.isArray(payload.entries) ? payload.entries : [];
    const forcedEntryIds: [string | undefined, string | undefined] = [
      typeof entries[0] === "string" ? entries[0] : undefined,
      typeof entries[1] === "string" ? entries[1] : undefined,
    ];
    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      forced_entry_ids: forcedEntryIds,
      status: forcedEntryIds[0] && forcedEntryIds[1] ? "ready" : "pending",
      source_event: event,
    });
    return;
  }

  if (event.event_type === "match_unassigned") {
    if (current.status === "voided") return;
    const nextStatus =
      current.status === "completed"
        ? "completed"
        : current.status === "in_progress"
          ? "ready"
          : current.winner_id
            ? "completed"
            : "ready";
    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      court_id: undefined,
      status: nextStatus,
      source_event: event,
    });
    return;
  }

  if (event.event_type === "match_started") {
    if (current.status === "voided") return;
    slots.set(slotId, {
      ...current,
      ...nextSlotCoordinates,
      status: "in_progress",
      started_at: current.started_at ?? event.created_at,
      source_event: event,
    });
  }
}

function materializeEventDefinedSlots(state: TournamentState, slots: Map<string, SlotState>) {
  for (const [slotId, slot] of slots.entries()) {
    if (slot.round === undefined || slot.match_index === undefined) continue;
    if (slot.round < 0 || slot.match_index < 0) continue;

    if (!state.rounds[slot.round]) {
      state.rounds[slot.round] = [];
    }

    const round = state.rounds[slot.round];
    const existingIndex = round.findIndex((match) => match?.[2]?.slot_id === slotId);
    if (existingIndex === -1) {
      round.push([
        null,
        null,
        {
          slot_id: slotId,
          round: slot.round,
          match_index: slot.match_index,
          division: slot.division,
        },
      ]);
      continue;
    }

    const existingMatch = round[existingIndex];
    const existingMetadata = existingMatch[2] || {};
    existingMatch[2] = {
      ...existingMetadata,
      slot_id: existingMetadata.slot_id || slotId,
      round: slot.round,
      match_index: slot.match_index,
      division: existingMetadata.division ?? slot.division,
    };
  }
}

function deriveState(
  competition: TournamentCompetition,
  baseState: TournamentState,
  slots: Map<string, SlotState>,
): TournamentState {
  const format = getFormat(competition.format as FormatName);
  let current = baseState;

  // Re-run transform until event-applied winners stabilize derived rounds.
  for (let i = 0; i < 16; i++) {
    const transformed = format.transform(current, competition.format_settings);
    // Synthetic slot materialization is only needed for formats that don't fully
    // define rounds from the state itself (Custom). On Swiss/Monrad with divisions
    // it can duplicate slot ids across divisions and create phantom completed cards.
    if (competition.format === "Custom") {
      materializeEventDefinedSlots(transformed, slots);
    }
    for (let roundIndex = 0; roundIndex < transformed.rounds.length; roundIndex++) {
      const round = transformed.rounds[roundIndex];
      for (let matchIndex = 0; matchIndex < round.length; matchIndex++) {
        const match = round[matchIndex];
        if (!match) continue;
        applySlotState(match, roundIndex, matchIndex, slots);
      }
    }
    if (deepEqual(transformed, current)) {
      return transformed;
    }
    current = transformed;
  }

  return current;
}

export function reduceTournamentState(params: {
  competition: TournamentCompetition;
  entries: TournamentEntry[];
  events: TournamentCompetitionEvent[];
  persistedConflicts?: TournamentCompetitionConflict[];
}): ReducedTournament {
  const { competition, entries, events, persistedConflicts = [] } = params;

  const baseState = buildInitialState(entries, competition.format_data || {});
  const sortedEvents = sortEvents(events);
  const slots = new Map<string, SlotState>();
  const detectedConflicts: TournamentConflict[] = [];

  for (const event of sortedEvents) {
    applyPlayoffDivisionEvent(event, baseState.entries);
    applyEventToSlots(event, slots, detectedConflicts);
  }

  const state = deriveState(competition, baseState, slots);
  const conflicts = mergeOpenConflicts(persistedConflicts, detectedConflicts);

  state.metadata = {
    ...(state.metadata || {}),
    conflicts,
  };

  return { state, conflicts };
}
